// On-demand mid-season Progress Report, opened from the header phase chip:
// the projected report card at the season's current pace + the front office
// ledger so far, with its own shareable PNG. Works in any league; challenge
// saves keep their player ID + invite code on the card.

import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { logEvent } from "../util/logEvent.ts";
import { challengeGradeColor } from "../util/challengeUi.ts";
import SummerLeagueMark from "../components/SummerLeagueMark.tsx";
import {
	FrontOfficeLedgerCard,
	ReportBars,
	ReportBitsStyles,
	TopPlayersCard,
} from "../components/ReportBits.tsx";
import type { View } from "../../common/types.ts";

type Props = View<"progressReport">;

// White, theme-independent PNG source. Same visual family as the challenge
// result card, but explicitly a snapshot: PROJECTED everywhere, "through N
// games" instead of a final record.
const ProgressShareCard = ({
	card,
	challengeCode,
	challengePlayerId,
	gp,
	ledger,
	lost,
	paceWon,
	players,
	projectedWins,
	teamName,
	won,
}: Props) => {
	if (!card) {
		return null;
	}
	const color = challengeGradeColor(card.grade);
	const over =
		paceWon !== undefined && projectedWins !== undefined
			? Math.round((paceWon - projectedWins) * 10) / 10
			: undefined;

	const chip = (k: string, v: string) => (
		<div
			style={{
				flex: 1,
				background: "#f4f6f7",
				borderRadius: 10,
				padding: "9px 6px",
				textAlign: "center",
			}}
		>
			<div
				style={{
					fontSize: 10,
					fontWeight: 800,
					letterSpacing: 0.3,
					color: "#8a949c",
				}}
			>
				{k}
			</div>
			<div
				style={{
					fontSize: 20,
					fontWeight: 900,
					color: "#121212",
					marginTop: 2,
				}}
			>
				{v}
			</div>
		</div>
	);

	return (
		<div
			style={{
				width: 540,
				fontFamily: "Montserrat, Helvetica, Arial, sans-serif",
				background: "#ffffff",
				borderRadius: 20,
				overflow: "hidden",
				boxSizing: "border-box",
			}}
		>
			<div style={{ padding: "24px 26px 0" }}>
				<div
					style={{
						display: "inline-block",
						background: "#121212",
						color: "#18E0A8",
						fontSize: 12,
						fontWeight: 800,
						letterSpacing: 1,
						padding: "6px 12px",
						borderRadius: 7,
					}}
				>
					{challengeCode
						? "EXECUTIVE CHALLENGE · PROGRESS REPORT"
						: "PROGRESS REPORT"}
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "flex-end",
						justifyContent: "space-between",
						marginTop: 22,
					}}
				>
					<div>
						<div
							style={{
								fontSize: 11,
								fontWeight: 800,
								letterSpacing: 0.8,
								color: "#8a949c",
							}}
						>
							PROJECTED
						</div>
						<div
							style={{
								fontSize: 76,
								fontWeight: 900,
								letterSpacing: -2,
								lineHeight: 0.9,
								color,
							}}
						>
							{card.grade}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								marginTop: 14,
							}}
						>
							{challengePlayerId ? (
								<div
									style={{ fontSize: 19, fontWeight: 900, color: "#121212" }}
								>
									{challengePlayerId}
								</div>
							) : null}
							<div style={{ fontSize: 15, fontWeight: 700, color: "#6b7680" }}>
								{challengePlayerId ? `· ${teamName}` : teamName}
							</div>
						</div>
						<div
							style={{
								fontSize: 15,
								fontWeight: 700,
								color: "#6b7680",
								marginTop: 3,
							}}
						>
							{won}–{lost} · through {gp} games
						</div>
					</div>
					<div style={{ textAlign: "right" }}>
						<div
							style={{
								fontSize: 12,
								fontWeight: 800,
								letterSpacing: 0.8,
								color: "#8a949c",
							}}
						>
							PROJ. GM SCORE
						</div>
						<div
							style={{
								fontSize: 56,
								fontWeight: 900,
								letterSpacing: -1.5,
								lineHeight: 1,
								color: "#121212",
							}}
						>
							{card.gmScore}
						</div>
					</div>
				</div>
				<div style={{ display: "flex", gap: 8, marginTop: 20 }}>
					{chip("RECORD", `${won}–${lost}`)}
					{chip("PACE", paceWon !== undefined ? `${paceWon} W` : "—")}
					{chip(
						"OVER",
						over !== undefined ? `${over > 0 ? "+" : ""}${over}` : "—",
					)}
				</div>
				{players.length > 0 ? (
					<div style={{ marginTop: 16 }}>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "1fr 40px 40px 48px 48px 48px",
								gap: 4,
								padding: "0 2px 6px",
								fontSize: 10,
								fontWeight: 800,
								letterSpacing: 0.5,
								color: "#8a949c",
							}}
						>
							<div>PLAYER</div>
							<div style={{ textAlign: "center" }}>OVR</div>
							<div style={{ textAlign: "center" }}>POT</div>
							<div style={{ textAlign: "center" }}>PPG</div>
							<div style={{ textAlign: "center" }}>RPG</div>
							<div style={{ textAlign: "center" }}>APG</div>
						</div>
						{players.slice(0, 5).map((p) => (
							<div
								key={p.pid}
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 40px 40px 48px 48px 48px",
									gap: 4,
									alignItems: "center",
									padding: "6px 2px",
									borderTop: "1px solid #e9ecef",
								}}
							>
								<div
									style={{
										fontSize: 13.5,
										fontWeight: 800,
										color: "#121212",
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{p.name}
								</div>
								<div
									style={{
										fontSize: 13.5,
										fontWeight: 900,
										color: "#121212",
										textAlign: "center",
									}}
								>
									{p.ovr}
								</div>
								<div
									style={{
										fontSize: 13,
										fontWeight: 700,
										color: "#8a949c",
										textAlign: "center",
									}}
								>
									{p.pot}
								</div>
								<div
									style={{
										fontSize: 13,
										fontWeight: 700,
										color: "#4b5563",
										textAlign: "center",
									}}
								>
									{p.pts.toFixed(1)}
								</div>
								<div
									style={{
										fontSize: 13,
										fontWeight: 700,
										color: "#4b5563",
										textAlign: "center",
									}}
								>
									{p.trb.toFixed(1)}
								</div>
								<div
									style={{
										fontSize: 13,
										fontWeight: 700,
										color: "#4b5563",
										textAlign: "center",
									}}
								>
									{p.ast.toFixed(1)}
								</div>
							</div>
						))}
					</div>
				) : null}
				{ledger.bestMove ? (
					<div
						style={{
							display: "flex",
							alignItems: "baseline",
							gap: 8,
							marginTop: 14,
							paddingTop: 12,
							borderTop: "1px solid #e9ecef",
						}}
					>
						<div
							style={{
								fontSize: 10,
								fontWeight: 800,
								letterSpacing: 0.6,
								color: "#8a949c",
								flex: "none",
							}}
						>
							BEST MOVE
						</div>
						<div
							style={{
								fontSize: 14,
								fontWeight: 800,
								color: "#121212",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{ledger.bestMove.name}
						</div>
						<div
							style={{
								fontSize: 14,
								fontWeight: 900,
								color: "#2bc98d",
								flex: "none",
							}}
						>
							+{ledger.bestMove.ewa} wins
						</div>
					</div>
				) : null}
			</div>
			<div
				style={{
					marginTop: 22,
					height: 4,
					background: "linear-gradient(90deg,#7C36E6,#FF3D86,#FF7A3C,#18E0A8)",
				}}
			/>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 11,
					padding: "16px 24px",
					background: "#121212",
				}}
			>
				<SummerLeagueMark size={30} idSuffix="progress" />
				<div style={{ lineHeight: 1, flex: 1 }}>
					<div
						style={{
							fontSize: 15,
							fontWeight: 900,
							color: "#fff",
							letterSpacing: 0.4,
						}}
					>
						SUMMER LEAGUE
					</div>
					<div
						style={{
							fontSize: 12,
							fontWeight: 700,
							color: "#8a97a0",
							letterSpacing: 0.3,
							marginTop: 3,
						}}
					>
						{challengeCode
							? `Executive Challenge · Code ${challengeCode}`
							: "Season in progress"}
					</div>
				</div>
			</div>
		</div>
	);
};

const ProgressReport = (props: Props) => {
	const {
		card,
		gp,
		ledger,
		lost,
		paceWon,
		players,
		projectedWins,
		teamName,
		won,
	} = props;
	useTitleBar({ title: "Progress Report" });

	const shareRef = useRef<HTMLDivElement>(null);
	const [sharing, setSharing] = useState(false);

	const onShare = async () => {
		const el = shareRef.current;
		if (!el || sharing) {
			return;
		}
		setSharing(true);
		try {
			const canvas = await html2canvas(el, {
				backgroundColor: null,
				scale: 2,
				logging: false,
			});
			const blob = await new Promise<Blob | null>((resolve) =>
				canvas.toBlob(resolve, "image/png"),
			);
			if (!blob) {
				throw new Error("Failed to render image");
			}
			const fileName = `progress_report_${teamName}`
				.replaceAll(/[^\da-z]+/gi, "_")
				.toLowerCase();
			const file = new File([blob], `${fileName}.png`, { type: "image/png" });

			const nav = navigator as Navigator & {
				canShare?: (data?: any) => boolean;
				share?: (data?: any) => Promise<void>;
			};
			if (nav.canShare?.({ files: [file] }) && nav.share) {
				try {
					await nav.share({
						files: [file],
						title: `${teamName} progress report`,
					});
					return;
				} catch (error) {
					if ((error as { name?: string }).name === "AbortError") {
						return;
					}
				}
			}

			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${fileName}.png`;
			document.body.append(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(url), 1500);
		} catch (error) {
			console.error(error);
			logEvent({
				type: "error",
				text: "Couldn't generate the progress card.",
				saveToDb: false,
			});
		} finally {
			setSharing(false);
		}
	};

	if (!card) {
		return (
			<div className="chr">
				<style>{styles}</style>
				<a className="chr-back" href={helpers.leagueUrl([])}>
					‹ Back
				</a>
				<div className="chr-eyebrow">PROGRESS REPORT</div>
				<div className="prg-pending">
					<div className="prg-pending-badge">📊</div>
					Not enough games yet — play at least 5 and check back for a projected
					grade.
				</div>
			</div>
		);
	}

	const color = challengeGradeColor(card.grade);

	return (
		<div className="chr">
			<style>{styles}</style>
			<ReportBitsStyles />
			<a className="chr-back" href={helpers.leagueUrl([])}>
				‹ Back
			</a>
			<div className="chr-eyebrow">PROGRESS REPORT</div>

			<div
				className="prg-hero"
				style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
			>
				<div className="prg-hero-circle" />
				<div className="prg-hero-row">
					<div>
						<div className="prg-hero-eyebrow">PROJECTED</div>
						<div className="prg-grade">{card.grade}</div>
						<div className="prg-hero-team">{teamName}</div>
					</div>
					<div className="prg-hero-score">
						<div className="prg-hero-score-label">PROJ. GM SCORE</div>
						<div className="prg-hero-score-num">{card.gmScore}</div>
					</div>
				</div>
				<div className="prg-asof">
					{won}–{lost} · through {gp} games
				</div>
			</div>

			<div className="chr-card">
				<div className="chr-card-label">HOW YOU’RE SCORING</div>
				<ReportBars
					card={card}
					won={paceWon ?? won}
					projectedWins={projectedWins}
					mode="pace"
				/>
			</div>

			<TopPlayersCard players={players} />

			<FrontOfficeLedgerCard ledger={ledger} />

			<div className="prg-note">
				📊 A snapshot at today’s pace — nothing here is locked in.
			</div>

			<button
				type="button"
				className="chr-share"
				onClick={onShare}
				disabled={sharing}
			>
				{sharing ? "Rendering…" : "📸 Share progress card"}
			</button>

			{/* Off-screen source for the shareable PNG. */}
			<div
				aria-hidden
				style={{ position: "fixed", left: -10000, top: 0, zIndex: -1 }}
			>
				<div ref={shareRef}>
					<ProgressShareCard {...props} />
				</div>
			</div>
		</div>
	);
};

const styles = `
.chr { max-width:560px; margin:0 auto; padding:12px 18px 40px; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; }
.chr-back { display:inline-block; font-size:13px; font-weight:800; color:#9fb0bc;
  text-decoration:none; padding:4px 0 12px; }
.chr-back:hover { color:#cfe0ec; }
.chr-eyebrow { font-size:11px; font-weight:800; letter-spacing:1.4px; color:#18E0A8; }

.prg-pending { margin-top:16px; background:#181818; border:1px solid #232323; border-radius:16px;
  padding:22px 20px; font-size:13px; font-weight:600; color:#9aa4ac; line-height:1.5;
  text-align:center; }
.prg-pending-badge { width:52px; height:52px; border-radius:50%; background:rgba(24,224,168,.16);
  border:1px solid rgba(24,224,168,.4); display:flex; align-items:center; justify-content:center;
  font-size:23px; margin:0 auto 12px; }

.prg-hero { margin-top:12px; border-radius:20px; padding:22px 20px; position:relative;
  overflow:hidden; }
.prg-hero-circle { position:absolute; right:-24px; top:-24px; width:130px; height:130px;
  border-radius:50%; background:rgba(255,255,255,.12); }
.prg-hero-row { position:relative; display:flex; align-items:center; justify-content:space-between; }
.prg-hero-eyebrow { font-size:9.5px; font-weight:800; letter-spacing:1.1px; color:#083a2a; }
.prg-grade { font-size:60px; font-weight:900; letter-spacing:-2px; line-height:.95; color:#062b1f; }
.prg-hero-team { font-size:12px; font-weight:900; color:#062b1f; margin-top:8px; }
.prg-hero-score { text-align:right; }
.prg-hero-score-label { font-size:10px; font-weight:800; letter-spacing:1px; color:#083a2a; }
.prg-hero-score-num { font-size:38px; font-weight:900; letter-spacing:-1px; line-height:1;
  color:#062b1f; }
.prg-asof { position:relative; margin-top:16px; padding-top:13px;
  border-top:1px solid rgba(6,43,31,.22); font-size:13px; font-weight:800; color:#062b1f; }

.prg-note { margin-top:12px; background:#161616; border:1px solid #232323; border-radius:14px;
  padding:13px 15px; font-size:12px; color:#9aa4ac; font-weight:600; line-height:1.45; }

.chr-share { display:block; width:100%; margin-top:16px; padding:15px; border:none;
  border-radius:26px; background:#18E0A8; color:#053a2c; font-size:14.5px; font-weight:900;
  text-align:center; cursor:pointer; font-family:inherit;
  box-shadow:0 10px 26px rgba(24,224,168,.28); }
.chr-share:disabled { background:#232323; color:#9aa4ac; box-shadow:none; cursor:default; }
`;

export default ProgressReport;
